import React from "react";
import { SignIn, SignUp, useUser } from "@clerk/clerk-react";
import "./AuthPage.css";

/**
 * Detect if we're on a mobile/touch device (iOS specifically is the problem).
 * We check for touch capability AND small viewport to avoid false positives
 * on laptops with touchscreens.
 */
function isMobileDevice() {
  return (
    ("ontouchstart" in window || navigator.maxTouchPoints > 0) &&
    window.innerWidth <= 968
  );
}

function AuthPage({ onLogin, defaultTab = "signin" }) {
  // Detect Clerk invitation ticket - forces signup tab for invited users
  const hasInviteTicket = new URLSearchParams(window.location.search).has('__clerk_ticket');

  const [isLogin, setIsLogin] = React.useState(
    hasInviteTicket ? false : defaultTab !== "signup"
  );
  const { user, isLoaded } = useUser();

  // If user is already signed in, call onLogin (only once)
  const hasCalledOnLogin = React.useRef(false);
  React.useEffect(() => {
    if (isLoaded && user && onLogin && !hasCalledOnLogin.current) {
      hasCalledOnLogin.current = true;
      onLogin(user);
    }
  }, [isLoaded, user, onLogin]);

  // ============================================================
  // iOS MOBILE OTP FIX
  // Clerk's OTP component has a hidden native <input> and visual
  // digit boxes. On iOS, the keyboard won't open because:
  //   1) The native input is hidden with clip/1px (CSS fix applied)
  //   2) Clerk focuses it programmatically, but iOS requires the
  //      focus to come from a direct user tap gesture
  //   3) iOS may blur the input immediately, causing the flash
  //
  // This fix uses MutationObserver to detect when OTP inputs appear
  // and patches them for iOS compatibility.
  // ============================================================
  React.useEffect(() => {
    if (!isMobileDevice()) return;

    let cleanupFns = [];

    function patchOtpField(otpContainer) {
      // Find the native hidden input inside Clerk's OTP component
      const nativeInput = otpContainer.querySelector(
        'input.cl-formFieldInput, input[name="code"], input[autocomplete="one-time-code"]'
      );
      if (!nativeInput || nativeInput.dataset.iosPatch === "true") return;
      nativeInput.dataset.iosPatch = "true";

      // Set attributes so iOS recognizes this as an OTP input
      nativeInput.setAttribute("autocomplete", "one-time-code");
      nativeInput.setAttribute("inputmode", "numeric");
      nativeInput.setAttribute("pattern", "[0-9]*");
      nativeInput.setAttribute("type", "text");

      // Force inline styles to override any CSS that hides it too aggressively
      // The input needs real dimensions for iOS to allow focus + keyboard
      Object.assign(nativeInput.style, {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        opacity: "0",
        zIndex: "10",
        fontSize: "16px", // Prevents iOS auto-zoom
        caretColor: "transparent",
        color: "transparent",
        background: "transparent",
        border: "none",
        margin: "0",
        padding: "0",
        // Remove any clip-based hiding
        clip: "auto",
        clipPath: "none",
        overflow: "visible",
        whiteSpace: "normal",
        WebkitAppearance: "none",
        WebkitUserSelect: "text",
        touchAction: "manipulation",
      });

      // Ensure the container is positioned so the overlay works
      otpContainer.style.position = "relative";
      otpContainer.style.overflow = "visible";


      // Add touch handlers to visual OTP boxes so tapping them
      // triggers a DIRECT focus (within the user gesture, so iOS allows it).
      // NOTE: We do NOT try to prevent blur — blur can't be cancelled and
      // attempting to re-focus from rAF/setTimeout fails iOS's gesture check.
      // Relying on direct gesture focus is the only reliable approach.
      const visualBoxes = otpContainer.querySelectorAll(
        ".cl-otpCodeFieldInput"
      );
      const touchHandler = (e) => {
        e.preventDefault(); // Prevent ghost-click and scroll
        nativeInput.focus(); // Must be synchronous & inside the gesture
      };

      visualBoxes.forEach((box) => {
        box.addEventListener("touchstart", touchHandler, { passive: false });
        box.addEventListener("click", touchHandler);
      });

      // Also handle taps on the container itself
      otpContainer.addEventListener("touchstart", (e) => {
        // Only if tapping within the inputs area
        if (e.target.closest(".cl-otpCodeFieldInputs")) {
          e.preventDefault();
          nativeInput.focus();
        }
      }, { passive: false });

      cleanupFns.push(() => {
        visualBoxes.forEach((box) => {
          box.removeEventListener("touchstart", touchHandler);
          box.removeEventListener("click", touchHandler);
        });
      });

    }

    // Scan for any OTP fields already in the DOM
    function scanForOtpFields() {
      document.querySelectorAll(".cl-otpCodeField").forEach(patchOtpField);
    }

    // Initial scan
    scanForOtpFields();

    // Watch for Clerk dynamically adding OTP fields (e.g., after password step → 2FA step)
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added node contains an OTP field
          for (const node of mutation.addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList?.contains("cl-otpCodeField")) {
                patchOtpField(node);
              } else if (node.querySelector?.(".cl-otpCodeField")) {
                node.querySelectorAll(".cl-otpCodeField").forEach(patchOtpField);
              }
            }
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"], // Catch Clerk class swaps mid-interaction
    });


    return () => {
      observer.disconnect();
      cleanupFns.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className="auth-page">
      <div className="auth-container hf-panel hf-reveal-scale hf-sheen">
        {/* Left Side - Branding */}
        <div className="auth-branding">
          <img
            src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
            alt="Optimus OS"
            className="auth-logo"
          />
          <h1 className="auth-title">Welcome to Optimus OS</h1>
          <p className="auth-subtitle">
            AI-powered business automation platform
          </p>

          <div className="feature-list hf-stagger">
            <div className="feature-item">
              <span className="feature-icon">🤖</span>
              <div className="feature-content">
                <h3>AI Automation</h3>
                <p>Automate your workflows with intelligent AI</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📊</span>
              <div className="feature-content">
                <h3>Business Intelligence</h3>
                <p>Make data-driven decisions with powerful analytics</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">💰</span>
              <div className="feature-content">
                <h3>Affiliate Program</h3>
                <p>Earn 30% recurring commission on referrals</p>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-icon">⚡</span>
              <div className="feature-content">
                <h3>Fast & Secure</h3>
                <p>Enterprise-grade security and performance</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Auth Form */}
        <div className="auth-form-container">
          <div className="clerk-auth-wrapper">
            <div className="auth-toggle">
              <button
                className={`toggle-btn hf-focus ${isLogin ? "active" : ""}`}
                onClick={() => setIsLogin(true)}
              >
                Sign In
              </button>
              <button
                className={`toggle-btn hf-focus ${!isLogin ? "active" : ""}`}
                onClick={() => setIsLogin(false)}
              >
                Sign Up
              </button>
            </div>

            {isLogin ? (
              <SignIn
                routing="hash"
                signUpUrl="#"
                afterSignInUrl="/"
                appearance={{
                  elements: {
                    // Hide Clerk branding
                    footer: { display: "none" },
                    footerAction: { display: "none" },
                    footerActionLink: { display: "none" },
                    footerActionText: { display: "none" },
                    identityPreviewText: { display: "none" },
                    formFooter: { display: "none" },

                    // Style card
                    rootBox: {
                      width: "100%",
                      maxWidth: "100%",
                    },
                    card: {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      padding: "0",
                    },
                  },
                  variables: {
                    colorPrimary: "#3b82f6",
                    colorText: "#ffffff",
                    colorTextSecondary: "rgba(255, 255, 255, 0.7)",
                    colorBackground: "transparent",
                    colorInputBackground: "rgba(255, 255, 255, 0.05)",
                    colorInputText: "#ffffff",
                    borderRadius: "8px",
                  },
                }}
              />
            ) : (
              <SignUp
                routing="hash"
                signInUrl="#"
                afterSignUpUrl="/"
                appearance={{
                  elements: {
                    // Hide Clerk branding
                    footer: { display: "none" },
                    footerAction: { display: "none" },
                    footerActionLink: { display: "none" },
                    footerActionText: { display: "none" },
                    identityPreviewText: { display: "none" },
                    formFooter: { display: "none" },

                    // Style card
                    rootBox: {
                      width: "100%",
                      maxWidth: "100%",
                    },
                    card: {
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      border: "none",
                      padding: "0",
                    },
                  },
                  variables: {
                    colorPrimary: "#3b82f6",
                    colorText: "#ffffff",
                    colorTextSecondary: "rgba(255, 255, 255, 0.7)",
                    colorBackground: "transparent",
                    colorInputBackground: "rgba(255, 255, 255, 0.05)",
                    colorInputText: "#ffffff",
                    borderRadius: "8px",
                  },
                }}
              />
            )}

            {/* Purchase CTA - shown to users who don't have an account yet */}
            <div className="auth-purchase-cta">
              <span className="auth-purchase-label">Don't have access yet?</span>
              <a
                href="https://activateoptimus.com"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-purchase-btn hf-press hf-focus"
              >
                🚀 Purchase your subscription here
              </a>
            </div>

            {/* Legal Footer */}
            <div className="auth-footer">
              <div className="auth-footer-links">
                <a href="/terms" className="footer-link">
                  Terms of Service
                </a>
                <span className="footer-separator">·</span>
                <a href="/privacy" className="footer-link">
                  Privacy Policy
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
